import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFixedTooltip } from '../../hooks/useFixedTooltip';
import { BdsNavTreeItem, BdsNavTreeGroup } from '@/core/constants/webComponents';
import { useMenuShadowEffects } from './useMenuShadowEffects';
import {
  bindMenuItemEvents,
  filterVisibleItems,
  getMenuItemClass,
  getMenuItemStyleByLevel,
  getOpenProps,
  getTooltipText,
  wrapMenuItemWithLink,
  wrapMenuItemWithTooltip,
} from './menuHelpers';

import './Menu.scss';

export type MenuItem = {
  id: string;
  label: string;
  path?: string;
  href?: string;
  icon?: string;
  secondaryText?: string;
  disabled?: boolean;
  disabledReason?: 'upgrade' | 'permission';
  visible?: boolean | (() => boolean);
  target?: string;
  defaultOpen?: boolean;
  children?: MenuItem[];
};

export type MenuVariant = 'default' | 'metrics';

interface MenuProps {
  items: MenuItem[];
  eventName?: string;
  onNavigate?: (path: string) => void;
  labels?: Record<string, string>;
  variant?: MenuVariant;
  initialActivePath?: string;
}

const DEFAULT_MENU_EVENT = 'mfe-menu-item-click';


export const Menu: React.FC<MenuProps> = ({
  items,
  eventName = DEFAULT_MENU_EVENT,
  onNavigate,
  variant = 'default',
  initialActivePath,
}) => {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const visibleItems = filterVisibleItems(items);
  const positionTooltip = useFixedTooltip('.menu__tooltip-container', 'right');
  const [activeItemPath, setActiveItemPath] = useState<string | null>(
    () => initialActivePath ?? globalThis.location?.pathname ?? null
  );

  useMenuShadowEffects(menuRef, visibleItems, activeItemPath, variant);

  const emitMenuClick = useCallback((item: MenuItem) => {
    const detail = {
      id: item.id,
      path: item.path,
      label: item.label,
    };

    const eventTarget = menuRef.current ?? document;
    eventTarget.dispatchEvent(
      new CustomEvent(eventName, {
        detail,
        bubbles: true,
        composed: true,
      }),
    );
  }, [eventName]);

  const handleItemClick = useCallback((item: MenuItem) => {
    if (item.disabled) {
      return;
    }

    if (item.path) {
      setActiveItemPath(item.path);
    }

    if (item.path && onNavigate) {
      onNavigate(item.path);
    }
    if (item.path) {
      emitMenuClick(item);
    }
  }, [onNavigate, emitMenuClick]);

  useEffect(() => {
    const menuElements = menuRef.current?.querySelectorAll('.menu__item, .menu__item-wrapper, .menu__item-link');
    const cleanups = Array.from(menuElements ?? []).map((el) => {
      return bindMenuItemEvents(el, visibleItems, handleItemClick, positionTooltip);
    });

    return () => cleanups.forEach(fn => fn());
  }, [visibleItems, positionTooltip, handleItemClick]);

  const renderMenuItem = (item: MenuItem, level = 0) => {
    const children = item.children ?? [];
    const itemStyle = getMenuItemStyleByLevel(level, variant);
    const itemClass = getMenuItemClass(item, level, variant);
    const openProps = getOpenProps(item, variant);
    const menuLabels = t('menu', { returnObjects: true }) as any;
    const tooltipText = getTooltipText(item, menuLabels);

    const navItem = (
      <BdsNavTreeItem
        key={item.id}
        class={itemClass}
        text={item.label}
        icon={item.icon}
        secondary-text={item.secondaryText}
        disable={item.disabled}
        {...openProps}
        data-test={`menu-item-${item.id}`}
        data-active={variant === 'metrics' && !!item.path && item.path === activeItemPath ? 'true' : undefined}
        style={itemStyle}
      >
        {children.length > 0 && children.map(child => renderMenuItem(child, level + 1))}
      </BdsNavTreeItem>
    );

    if (tooltipText) {
      return wrapMenuItemWithTooltip(item, navItem, tooltipText);
    }

    if (item.path && item.href && !item.disabled) {
      return wrapMenuItemWithLink(item, navItem);
    }

    return navItem;
  };

  return (
    <div className={`menu menu--${variant}`} ref={menuRef}>
      <BdsNavTreeGroup collapse={variant === 'metrics' ? 'multiple' : 'single'} class="menu__items">
        {visibleItems.map(item => renderMenuItem(item))}
      </BdsNavTreeGroup>
    </div>
  );
};
